import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { createConsentAwareJSONStorage } from '../utils/consentAwareStorage';
import type { ImageState, GeneratedImage, ActiveImageGeneration } from '../types';
import type { PaletteColor } from '../components/Image/ImageComposer/constants';
import { DEFAULT_IMAGE_PROVIDER, DEFAULT_IMAGE_MODEL, IMAGE_MODELS_CONFIG } from '../config/models';

// Максимальное количество одновременно генерируемых изображений (дефолт для free tier)
const MAX_CONCURRENT_GENERATIONS = 1;

// Флаг для пропуска синхронизации с БД при регенерации из галереи
// Когда GalleryPage устанавливает файлы напрямую в стор, sync-хук не должен их перезаписывать
let _skipAttachmentsSync = false;
export const setSkipAttachmentsSync = (skip: boolean) => { _skipAttachmentsSync = skip; };
export const getSkipAttachmentsSync = () => _skipAttachmentsSync;

const useImageStore = create<ImageState>()(
  persist(
    (set) => ({
      images: [],
      currentProvider: DEFAULT_IMAGE_PROVIDER,
      currentModel: DEFAULT_IMAGE_MODEL,
      currentSize: '1:1', // Дефолтный формат (соответствует 1024x1024)
      currentQuality: 'medium',
      currentResolution: '1K',
      currentFormat: 'png',
      currentCompression: 80,
      currentBackground: 'opaque',
      currentCount: 1, // Количество изображений для генерации
      currentInputFidelity: 'high', // Для GPT Image 1
      currentStyle: 'natural', // Для GPT Image 1.5 (всегда естественный)
      currentPrompt: '', // Сохраняемый промпт пользователя
      selectedPresetId: null, // ID выбранного пресета
      customPresetName: null, // Название выбранного кастомного пресета (для отображения вместо промпта)
      customPresetHint: null, // Подсказка для композера от кастомного пресета
      modelPresets: {}, // Пресеты для каждой модели
      modelSettings: {}, // Настройки Composer для каждой модели (size, quality, count, background, fidelity)
      attachedFiles: [],
      isGenerating: false,
      activeGenerations: 0,
      activeGenerationDetails: [],
      maxConcurrentGenerations: MAX_CONCURRENT_GENERATIONS,
      totalGeneratingImages: 0,
      
      // Higgsfield-specific state
      higgsfieldStyleId: null,
      higgsfieldStyleStrength: 1,
      higgsfieldCustomReferenceId: null,
      higgsfieldCustomReferenceStrength: 1,
      higgsfieldEnhancePrompt: false,
      higgsfieldSeed: null,
      higgsfieldAvailableStyles: [],
      higgsfieldFavoriteStyleIds: [],
      modelHiggsfieldStyles: {}, // Higgsfield стили для каждой модели

      // Recraft-specific state
      vectorMode: false, // SVG vector mode для Recraft V4

      // Nano Banana 2 fast mode
      fastMode: false,

      // Color Palette state
      colorPalette: null as PaletteColor[] | null, // Selected palette (null = no palette)
      colorPalettePresetId: null as string | null, // ID of selected preset palette (or null for custom)

      // Cinematic Camera state (deterministic camera params)
      cinematicCameraId: 'sony-a7iv',
      cinematicFocalId: 'f50',
      cinematicApertureId: 'a1.4',
      cinematicCameraEnabled: false,

      // Studio Elements (shared DB with video elements)
      selectedElements: [],
      userElements: [] as import('../services/api/user-elements-api').UserElement[],
      userElementsLoaded: false,
      userElementsLimit: 1,

      // Midjourney-specific state
      midjourneyStylize: 100,
      midjourneyChaos: 0,
      midjourneyWeird: 0,
      midjourneySeed: -1,
      midjourneyNiji: 'close' as 'close' | '5' | '6',
      midjourneyQuality: 1,

      // Pending retry prompt for error card auto-regeneration
      pendingRetryPrompt: null,

      // Team mode: триггер для принудительного обновления при получении SSE событий
      teamRefreshTrigger: 0,

      // Pagination state
      hasMoreImages: true,
      currentOffset: 0,
      isLoadingMore: false,
      
      // SWR Cache state - для оптимизации загрузки при переключении вкладок
      lastLoadedAt: null as number | null, // Timestamp последней загрузки
      cacheKey: null as string | null, // Ключ кэша (team_id + folder_id + filter)
      isRevalidating: false, // Фоновое обновление данных

      addImage: (image: GeneratedImage) => 
        set((state) => ({ 
          images: [image, ...state.images] 
        })),

      addImages: (images: GeneratedImage[]) =>
        set((state) => {
          // Фильтруем дубликаты по ID
          const existingIds = new Set(state.images.map(img => img.id));
          const uniqueNewImages = images.filter(img => !existingIds.has(img.id));
          return {
            images: [...uniqueNewImages, ...state.images]
          };
        }),

      // Добавить изображения в конец списка (для подгрузки при скролле)
      appendImages: (images: GeneratedImage[]) =>
        set((state) => {
          // Фильтруем дубликаты по ID
          const existingIds = new Set(state.images.map(img => img.id));
          const uniqueNewImages = images.filter(img => !existingIds.has(img.id));
          return {
            images: [...state.images, ...uniqueNewImages]
          };
        }),

      setImages: (images: GeneratedImage[]) =>
        set({ images }),

      // Pagination actions
      setHasMoreImages: (hasMore: boolean) =>
        set({ hasMoreImages: hasMore }),

      setCurrentOffset: (offset: number) =>
        set({ currentOffset: offset }),

      setIsLoadingMore: (isLoading: boolean) =>
        set({ isLoadingMore: isLoading }),

      resetPagination: () =>
        set({ hasMoreImages: true, currentOffset: 0, isLoadingMore: false }),

      // SWR Cache actions
      setLastLoadedAt: (timestamp: number | null) =>
        set({ lastLoadedAt: timestamp }),

      setCacheKey: (key: string | null) =>
        set({ cacheKey: key }),

      setIsRevalidating: (isRevalidating: boolean) =>
        set({ isRevalidating }),

      // Инвалидация кэша (например, после генерации нового изображения)
      invalidateCache: () =>
        set({ lastLoadedAt: null }),

      clearImages: () => 
        set({ images: [] }),

      removeImage: (id: string) =>
        set((state) => ({
          images: state.images.filter(img => img.id !== id)
        })),

      setProvider: (provider: string) => 
        set((state) => {
          // При смене провайдера восстанавливаем сохраненные настройки для этого провайдера
          const savedSettings = state.modelSettings[provider];
          
          // Получаем конфигурацию нового провайдера
          const providerModels = IMAGE_MODELS_CONFIG[provider];
          const modelConfig = providerModels?.[0]; // Первая модель провайдера
          const allowedCounts = modelConfig?.count || [1];
          const allowedQualities = modelConfig?.quality || [];
          const allowedResolutions = (modelConfig as { resolutions?: string[] } | undefined)?.resolutions || [];
          const allowedFormats = modelConfig?.formats || [];

          // Проверяем, поддерживается ли текущий count новым провайдером
          const currentCount = savedSettings?.count || state.currentCount;
          const validCount = allowedCounts.includes(currentCount) ? currentCount : allowedCounts[0];

          // Проверяем, поддерживается ли текущий size (aspect ratio) новым провайдером
          const currentSize = savedSettings?.size || state.currentSize;
          const validSize = allowedFormats.length > 0 && !allowedFormats.includes(currentSize)
            ? allowedFormats[0]
            : currentSize;
          
          // Проверяем, поддерживается ли текущее качество новым провайдером
          const currentQuality = savedSettings?.quality || state.currentQuality;
          let validQuality: typeof state.currentQuality = currentQuality;
          if (allowedQualities.length > 0 && !allowedQualities.includes(currentQuality)) {
            validQuality = allowedQualities[0] as typeof state.currentQuality; // Первое доступное качество
          }

          // Проверяем resolution (для моделей с отдельной осью разрешения, напр. gpt-image-2)
          const currentResolutionCandidate = (savedSettings as { resolution?: string } | undefined)?.resolution || state.currentResolution;
          let validResolution = currentResolutionCandidate;
          if (allowedResolutions.length > 0 && !allowedResolutions.includes(currentResolutionCandidate)) {
            validResolution = allowedResolutions[0];
          }

          if (savedSettings) {
            return {
              currentProvider: provider,
              currentSize: validSize,
              currentQuality: validQuality,
              currentResolution: validResolution,
              currentCount: validCount,
              currentBackground: savedSettings.background || state.currentBackground,
              currentInputFidelity: savedSettings.inputFidelity || state.currentInputFidelity,
              // Восстанавливаем Higgsfield настройки
              higgsfieldStyleId: savedSettings.higgsfieldStyleId !== undefined ? savedSettings.higgsfieldStyleId : state.higgsfieldStyleId,
              higgsfieldStyleStrength: savedSettings.higgsfieldStyleStrength || state.higgsfieldStyleStrength,
              higgsfieldEnhancePrompt: savedSettings.higgsfieldEnhancePrompt !== undefined ? savedSettings.higgsfieldEnhancePrompt : state.higgsfieldEnhancePrompt,
              higgsfieldSeed: savedSettings.higgsfieldSeed !== undefined ? savedSettings.higgsfieldSeed : state.higgsfieldSeed,
              // Восстанавливаем Recraft vector mode
              vectorMode: savedSettings.vectorMode !== undefined ? savedSettings.vectorMode : false,
              // Восстанавливаем Nano Banana 2 fast mode
              fastMode: savedSettings.fastMode !== undefined ? savedSettings.fastMode : false,
              // Восстанавливаем Color Palette
              colorPalette: savedSettings.colorPalette !== undefined ? savedSettings.colorPalette : null,
              colorPalettePresetId: savedSettings.colorPalettePresetId !== undefined ? savedSettings.colorPalettePresetId : null,
            };
          }
          return {
            currentProvider: provider,
            currentSize: validSize,
            currentQuality: validQuality,
            currentResolution: validResolution,
            currentCount: validCount,
            vectorMode: false,
            fastMode: false,
            colorPalette: null,
            colorPalettePresetId: null,
          };
        }),

      setModel: (model: string) => 
        set((state) => {
          // Получаем конфигурацию новой модели
          const providerModels = IMAGE_MODELS_CONFIG[state.currentProvider];
          const modelConfig = providerModels?.find(m => m.value === model) || providerModels?.[0];
          const allowedCounts = modelConfig?.count || [1];
          
          // Проверяем, поддерживается ли текущий count новой моделью
          const validCount = allowedCounts.includes(state.currentCount) ? state.currentCount : allowedCounts[0];
          
          return {
            currentModel: model,
            currentCount: validCount,
            selectedPresetId: state.modelPresets[model] || null,
            higgsfieldStyleId: state.modelHiggsfieldStyles[model] || null
          };
        }),

      setSize: (size: string) => 
        set((state) => ({
          currentSize: size,
          modelSettings: {
            ...state.modelSettings,
            [state.currentProvider]: {
              ...state.modelSettings[state.currentProvider],
              size
            }
          }
        })),

      setQuality: (quality: string) =>
        set((state) => ({
          currentQuality: quality,
          modelSettings: {
            ...state.modelSettings,
            [state.currentProvider]: {
              ...state.modelSettings[state.currentProvider],
              quality
            }
          }
        })),

      setResolution: (resolution: string) =>
        set((state) => ({
          currentResolution: resolution,
          modelSettings: {
            ...state.modelSettings,
            [state.currentProvider]: {
              ...state.modelSettings[state.currentProvider],
              resolution
            }
          }
        })),

      setFormat: (format: string) => 
        set({ currentFormat: format }),

      setCompression: (compression: number) =>
        set({ currentCompression: compression }),

      setBackground: (background: 'transparent' | 'opaque' | 'auto') =>
        set((state) => ({
          currentBackground: background,
          modelSettings: {
            ...state.modelSettings,
            [state.currentProvider]: {
              ...state.modelSettings[state.currentProvider],
              background
            }
          }
        })),

      setCount: (count: number) =>
        set((state) => ({
          currentCount: count,
          modelSettings: {
            ...state.modelSettings,
            [state.currentProvider]: {
              ...state.modelSettings[state.currentProvider],
              count
            }
          }
        })),

      setInputFidelity: (fidelity: 'low' | 'high') =>
        set((state) => ({
          currentInputFidelity: fidelity,
          modelSettings: {
            ...state.modelSettings,
            [state.currentProvider]: {
              ...state.modelSettings[state.currentProvider],
              inputFidelity: fidelity
            }
          }
        })),

      setStyle: (style: 'vivid' | 'natural') =>
        set({ currentStyle: style }),

      setPrompt: (prompt: string) =>
        set({ currentPrompt: prompt }),

      setSelectedPreset: (presetId: string | null, customName?: string | null, customHint?: string | null) =>
        set((state) => ({
          selectedPresetId: presetId,
          customPresetName: customName ?? null, // Название кастомного пресета (или null для официальных)
          customPresetHint: customHint ?? null, // Подсказка для композера
          modelPresets: {
            ...state.modelPresets,
            [state.currentModel]: presetId
          }
        })),

      setAttachedFiles: (files: any[] | ((prevFiles: any[]) => any[])) =>
        set((state) => ({
          attachedFiles: typeof files === 'function' ? files(state.attachedFiles) : files
        })),

      addAttachedFile: (file: any) =>
        set((state) => ({
          attachedFiles: [...state.attachedFiles, file]
        })),

      removeAttachedFile: (index: number) =>
        set((state) => ({
          attachedFiles: state.attachedFiles.filter((_, i) => i !== index)
        })),

      clearAttachedFiles: () =>
        set({ attachedFiles: [] }),

      // Очистка всех параметров генерации после успешной генерации
      clearGenerationState: () =>
        set({
          attachedFiles: [],
          higgsfieldSeed: null,
          selectedPresetId: null,
          customPresetName: null,
          customPresetHint: null,
          selectedElements: [],
        }),

      setIsGenerating: (isGenerating: boolean) => 
        set({ isGenerating }),

      incrementActiveGenerations: (provider: string, model: string, imageCount = 1, quality?: string, size?: string, details?: Partial<ActiveImageGeneration>) =>
        set((state) => {
          // Создаем новые генерации с уникальными ID и параметрами для точного расчета времени
          const newGenerations: ActiveImageGeneration[] = [];
          // Используем переданный batchId или генерируем новый для группы
          const batchId = details?.batchId || `batch-${Date.now()}-${Math.random()}`;
          
          for (let i = 0; i < imageCount; i++) {
            newGenerations.push({
              // Если переданы детали (из API) - используем их, иначе генерируем
              id: details?.id ? `${details.id}-${i}` : `img-gen-${Date.now()}-${Math.random()}-${i}`,
              provider: details?.provider || provider,
              model: details?.model || model,
              timestamp: details?.timestamp || Date.now(),
              status: details?.status || 'queue', // Начинаем с очереди
              // Используем переданные параметры или текущие из state
              quality: details?.quality || quality || state.currentQuality,
              size: details?.size || size || state.currentSize,
              hasAttachedFiles: details?.hasAttachedFiles ?? state.attachedFiles.length > 0,
              batchSize: details?.batchSize || imageCount, // Сохраняем общее количество изображений в batch
              batchId, // ID группы для обновления статуса
              prompt: details?.prompt || state.currentPrompt, // Промпт для отображения на карточке
              // TEAM MODE: сохраняем информацию о пользователе
              isTeamMember: details?.isTeamMember,
              userId: details?.userId,
              userName: details?.userName,
              userPhoto: details?.userPhoto,
              folderId: details?.folderId
            });
          }
          
          const newDetails = [...state.activeGenerationDetails, ...newGenerations];
          // Для team member генераций не увеличиваем счётчик (они не занимают слот текущего пользователя)
          const countToAdd = details?.isTeamMember ? 0 : imageCount;
          const newCount = state.activeGenerations + countToAdd;
          const newTotalImages = state.totalGeneratingImages + countToAdd;
          
          return {
            activeGenerations: newCount,
            activeGenerationDetails: newDetails,
            totalGeneratingImages: newTotalImages,
            isGenerating: newCount > 0 || newDetails.length > 0
          };
        }),

      decrementActiveGenerations: (imageCount = 1, batchId?: string) =>
        set((state) => {
          let newDetails: ActiveImageGeneration[];
          let removedCount: number;
          
          if (batchId) {
            // Удаляем генерации по batchId
            const toRemove = state.activeGenerationDetails.filter(gen => gen.batchId === batchId);
            removedCount = toRemove.filter(gen => !gen.isTeamMember).length;
            newDetails = state.activeGenerationDetails.filter(gen => gen.batchId !== batchId);
          } else {
            // Старая логика: удаляем самые старые свои генерации
            let removed = 0;
            newDetails = state.activeGenerationDetails.filter(gen => {
              if (gen.isTeamMember) return true;
              if (removed < imageCount) {
                removed++;
                return false;
              }
              return true;
            });
            removedCount = removed;
          }
          
          const newCount = Math.max(0, state.activeGenerations - removedCount);
          const newTotalImages = Math.max(0, state.totalGeneratingImages - removedCount);
          
          return {
            activeGenerations: newCount,
            activeGenerationDetails: newDetails,
            totalGeneratingImages: newTotalImages,
            isGenerating: newCount > 0 || newDetails.length > 0
          };
        }),

      // Обновление статуса генерации по ID
      updateGenerationStatus: (generationId: string, status: 'queue' | 'generating' | 'loading' | 'error') =>
        set((state) => ({
          activeGenerationDetails: state.activeGenerationDetails.map(gen =>
            gen.id === generationId ? { ...gen, status } : gen
          )
        })),

      // Обновление статуса всех генераций в batch
      updateBatchStatus: (batchId: string, status: 'queue' | 'generating' | 'loading' | 'error') =>
        set((state) => ({
          activeGenerationDetails: state.activeGenerationDetails.map(gen =>
            gen.batchId === batchId ? { ...gen, status } : gen
          )
        })),

      setMaxConcurrentGenerations: (max: number) => set({ maxConcurrentGenerations: max }),
      
      // Higgsfield-specific actions
      setHiggsfieldStyleId: (styleId: string | null) =>
        set((state) => ({
          higgsfieldStyleId: styleId,
          modelHiggsfieldStyles: {
            ...state.modelHiggsfieldStyles,
            [state.currentModel]: styleId
          },
          modelSettings: {
            ...state.modelSettings,
            [state.currentProvider]: {
              ...state.modelSettings[state.currentProvider],
              higgsfieldStyleId: styleId
            }
          }
        })),
      
      setHiggsfieldStyleStrength: (strength: number) =>
        set((state) => ({
          higgsfieldStyleStrength: Math.max(0, Math.min(1, strength)),
          modelSettings: {
            ...state.modelSettings,
            [state.currentProvider]: {
              ...state.modelSettings[state.currentProvider],
              higgsfieldStyleStrength: Math.max(0, Math.min(1, strength))
            }
          }
        })),
      
      setHiggsfieldCustomReferenceId: (referenceId: string | null) =>
        set({ higgsfieldCustomReferenceId: referenceId }),
      
      setHiggsfieldCustomReferenceStrength: (strength: number) =>
        set({ higgsfieldCustomReferenceStrength: Math.max(0, Math.min(1, strength)) }),
      
      setHiggsfieldEnhancePrompt: (enhance: boolean) =>
        set((state) => ({
          higgsfieldEnhancePrompt: enhance,
          modelSettings: {
            ...state.modelSettings,
            [state.currentProvider]: {
              ...state.modelSettings[state.currentProvider],
              higgsfieldEnhancePrompt: enhance
            }
          }
        })),
      
      setHiggsfieldSeed: (seed: number | null) =>
        set((state) => ({
          higgsfieldSeed: seed ? Math.max(1, Math.min(1000000, seed)) : null,
          modelSettings: {
            ...state.modelSettings,
            [state.currentProvider]: {
              ...state.modelSettings[state.currentProvider],
              higgsfieldSeed: seed ? Math.max(1, Math.min(1000000, seed)) : null
            }
          }
        })),
      
      setHiggsfieldAvailableStyles: (styles) =>
        set({ higgsfieldAvailableStyles: styles }),
      
      toggleHiggsfieldFavoriteStyle: (styleId: string) =>
        set((state) => {
          const isFavorite = state.higgsfieldFavoriteStyleIds.includes(styleId);
          return {
            higgsfieldFavoriteStyleIds: isFavorite
              ? state.higgsfieldFavoriteStyleIds.filter(id => id !== styleId)
              : [...state.higgsfieldFavoriteStyleIds, styleId]
          };
        }),
      
      // Recraft-specific: vector mode toggle
      setVectorMode: (vectorMode: boolean) =>
        set((state) => ({
          vectorMode,
          modelSettings: {
            ...state.modelSettings,
            [state.currentProvider]: {
              ...state.modelSettings[state.currentProvider],
              vectorMode
            }
          }
        })),

      // Nano Banana 2: fast mode toggle
      setFastMode: (fastMode: boolean) =>
        set((state) => ({
          fastMode,
          modelSettings: {
            ...state.modelSettings,
            [state.currentProvider]: {
              ...state.modelSettings[state.currentProvider],
              fastMode
            }
          }
        })),

      // Color Palette actions
      setColorPalette: (palette: PaletteColor[] | null, presetId?: string | null) =>
        set((state) => ({
          colorPalette: palette,
          colorPalettePresetId: presetId ?? null,
          modelSettings: {
            ...state.modelSettings,
            [state.currentProvider]: {
              ...state.modelSettings[state.currentProvider],
              colorPalette: palette,
              colorPalettePresetId: presetId ?? null,
            }
          }
        })),

      clearColorPalette: () =>
        set((state) => ({
          colorPalette: null,
          colorPalettePresetId: null,
          modelSettings: {
            ...state.modelSettings,
            [state.currentProvider]: {
              ...state.modelSettings[state.currentProvider],
              colorPalette: null,
              colorPalettePresetId: null,
            }
          }
        })),

      // Cinematic Camera actions
      setCinematicCamera: (cameraId: string, focalId: string, apertureId: string) =>
        set({ cinematicCameraId: cameraId, cinematicFocalId: focalId, cinematicApertureId: apertureId, cinematicCameraEnabled: true }),
      setCinematicCameraEnabled: (enabled: boolean) =>
        set({ cinematicCameraEnabled: enabled }),

      // Studio Elements actions
      selectElement: (element) =>
        set((state) => ({
          selectedElements: state.selectedElements.some(el => el.id === element.id)
            ? state.selectedElements
            : [...state.selectedElements, element]
        })),
      deselectElement: (elementId) =>
        set((state) => ({
          selectedElements: state.selectedElements.filter(el => el.id !== elementId)
        })),
      clearSelectedElements: () =>
        set({ selectedElements: [] }),
      setUserElements: (elements: import('../services/api/user-elements-api').UserElement[], limit?: number) =>
        set({ userElements: elements, userElementsLoaded: true, ...(limit !== undefined && { userElementsLimit: limit }) }),
      addUserElement: (element: import('../services/api/user-elements-api').UserElement) =>
        set((state) => ({ userElements: [...state.userElements, element] })),
      removeUserElement: (elementId: string) =>
        set((state) => ({ userElements: state.userElements.filter(el => el.id !== elementId) })),

      // Midjourney-specific actions
      setMidjourneyStylize: (value: number) =>
        set({ midjourneyStylize: Math.max(0, Math.min(1000, value)) }),
      setMidjourneyChaos: (value: number) =>
        set({ midjourneyChaos: Math.max(0, Math.min(100, value)) }),
      setMidjourneyWeird: (value: number) =>
        set({ midjourneyWeird: Math.max(0, Math.min(3000, value)) }),
      setMidjourneySeed: (value: number) =>
        set({ midjourneySeed: value }),
      setMidjourneyNiji: (value: 'close' | '5' | '6') =>
        set({ midjourneyNiji: value }),
      setMidjourneyQuality: (value: number) => {
        const allowed = [0.25, 0.5, 1, 2];
        const closest = allowed.reduce((prev, curr) => Math.abs(curr - value) < Math.abs(prev - value) ? curr : prev);
        set({ midjourneyQuality: closest });
      },

      // Pending retry prompt for error card auto-regeneration
      setPendingRetryPrompt: (prompt: string | null) =>
        set({ pendingRetryPrompt: prompt }),

      // Team mode: тригер для принудительного обновления при SSE событиях
      triggerTeamRefresh: () =>
        set((state) => ({
          teamRefreshTrigger: state.teamRefreshTrigger + 1
        })),

    }),
    {
      name: 'image-storage',
      storage: createConsentAwareJSONStorage(), // .ru: гейтится functional consent (FZ-152)
      version: 1, // Версия для миграции
      partialize: (state) => ({
        // images НЕ сохраняем - будут только после S3 интеграции
        // isGenerating, activeGenerations, activeGenerationDetails, totalGeneratingImages НЕ сохраняем - это runtime состояние
        currentProvider: state.currentProvider,
        currentModel: state.currentModel,
        currentSize: state.currentSize,
        currentQuality: state.currentQuality,
        currentResolution: state.currentResolution,
        currentFormat: state.currentFormat,
        currentCompression: state.currentCompression,
        currentBackground: state.currentBackground,
        currentCount: state.currentCount,
        currentInputFidelity: state.currentInputFidelity,
        currentStyle: state.currentStyle,
        currentPrompt: state.currentPrompt,
        selectedPresetId: state.selectedPresetId,
        modelPresets: state.modelPresets,
        modelSettings: state.modelSettings,
        // Higgsfield parameters
        higgsfieldStyleId: state.higgsfieldStyleId,
        higgsfieldStyleStrength: state.higgsfieldStyleStrength,
        higgsfieldCustomReferenceId: state.higgsfieldCustomReferenceId,
        higgsfieldCustomReferenceStrength: state.higgsfieldCustomReferenceStrength,
        higgsfieldEnhancePrompt: state.higgsfieldEnhancePrompt,
        higgsfieldSeed: state.higgsfieldSeed,
        higgsfieldFavoriteStyleIds: state.higgsfieldFavoriteStyleIds,
        modelHiggsfieldStyles: state.modelHiggsfieldStyles,
        // Recraft parameters
        vectorMode: state.vectorMode,
        // Nano Banana 2 fast mode
        fastMode: state.fastMode,
        // Color Palette
        colorPalette: state.colorPalette,
        colorPalettePresetId: state.colorPalettePresetId,
        // Cinematic Camera
        cinematicCameraId: state.cinematicCameraId,
        cinematicFocalId: state.cinematicFocalId,
        cinematicApertureId: state.cinematicApertureId,
        cinematicCameraEnabled: state.cinematicCameraEnabled,
        // selectedElements NOT persisted — loaded from API each time
        // Midjourney parameters
        midjourneyStylize: state.midjourneyStylize,
        midjourneyChaos: state.midjourneyChaos,
        midjourneyWeird: state.midjourneyWeird,
        midjourneySeed: state.midjourneySeed,
        midjourneyNiji: state.midjourneyNiji,
        midjourneyQuality: state.midjourneyQuality,
        // НЕ сохраняем attachedFiles в localStorage - используем только БД для синхронизации
      }),
      // Миграция старых значений
      migrate: (persistedState: any, version: number) => {
        if (version === 0) {
          // Миграция с версии 0 на 1: преобразуем 720p/1080p в Basic/High
          const qualityMap: Record<string, string> = {
            '720p': 'Basic',
            '1080p': 'High'
          };

          // Мигрируем currentQuality
          if (persistedState.currentQuality && qualityMap[persistedState.currentQuality]) {
            persistedState.currentQuality = qualityMap[persistedState.currentQuality];
          }

          // Мигрируем modelSettings для higgsfield-soul
          if (persistedState.modelSettings && persistedState.modelSettings['higgsfield-soul']) {
            const settings = persistedState.modelSettings['higgsfield-soul'];
            if (settings.quality && qualityMap[settings.quality]) {
              settings.quality = qualityMap[settings.quality];
            }
          }
        }

        // Миграция recraftv4-pro → recraftv4 с quality '2K'
        if (persistedState.currentProvider === 'recraftv4-pro') {
          persistedState.currentProvider = 'recraftv4';
          persistedState.currentQuality = '2K';
        }
        if (persistedState.modelSettings?.['recraftv4-pro']) {
          const proSettings = persistedState.modelSettings['recraftv4-pro'];
          persistedState.modelSettings['recraftv4'] = {
            ...persistedState.modelSettings['recraftv4'],
            ...proSettings,
            quality: '2K',
          };
          delete persistedState.modelSettings['recraftv4-pro'];
        }

        return persistedState;
      }
    }
  )
);

// SWR Cache helper functions (вне store для избежания проблем с типизацией)
const isCacheValid = (key: string): boolean => {
  const state = useImageStore.getState();
  if (state.cacheKey !== key) return false;
  if (!state.lastLoadedAt) return false;
  const cacheAge = Date.now() - state.lastLoadedAt;
  return cacheAge < 30000; // 30 секунд
};

const hasCachedData = (key: string): boolean => {
  const state = useImageStore.getState();
  return state.cacheKey === key && state.images.length > 0;
};

export { useImageStore, isCacheValid, hasCachedData };

